import { useCallback, useRef, useState } from 'react'
import { GLYPHS } from 'components/crm/ui/emptyState/drawnGlyphs'
import Glint, { glintPosition } from 'components/crm/ui/emptyState/Glint'
import { glintWindow, smooth, useAnimationFrame, usePrefersReducedMotion } from 'components/crm/ui/emptyState/glyphMotion'

const CYCLE = 4200

const ERASE = 0.8
const ERASE_SPAN = 0.08
const ERASE_STAGGER = 0.012

const DRAWN = 0.7

const STILL = { kind: null, at: [0, 1] }

const finishedAt = ({ strokes, dots = [], gesture = STILL }) =>
  Math.max(...strokes.map((stroke) => stroke.draw[1]), ...dots.map((dot) => dot.at[1]), gesture.kind ? gesture.at[1] : 0)

const gestureMotion = (kind, p) => {
  const move = { x: 0, y: 0, turn: 0 }
  if (!(p > 0 && p < 1)) return move
  const arch = Math.sin(Math.PI * p)
  if (kind === 'twist') {
    move.turn = p < 0.3 ? -24 * smooth(p / 0.3) : p < 0.6 ? -24 + 32 * smooth((p - 0.3) / 0.3) : 8 - 8 * smooth((p - 0.6) / 0.4)
  } else if (kind === 'shake') {
    const swing = Math.sin(6 * Math.PI * p) * (1 - p)
    move.x = 2.5 * swing
    move.turn = 10 * swing
  } else if (kind === 'drop') move.y = 3.5 * Math.exp(-2.5 * p) * Math.sin(3 * Math.PI * p)
  else if (kind === 'hop') move.y = -4 * arch
  else if (kind === 'drive') {
    move.x = 3 * arch
    move.turn = -6 * Math.sin(2 * Math.PI * p) * (1 - p)
  } else if (kind === 'toss') {
    move.y = -4.5 * arch
    move.turn = -18 * arch
  }
  return move
}

const frameAt = (glyph, clock) => {
  const { strokes, dots = [], gesture = STILL } = glyph
  const glint = glintWindow(finishedAt(glyph), CYCLE)
  const lift = Math.max(ERASE, glint[1])
  const [start, end] = gesture.at
  const move = gestureMotion(gesture.kind, (clock - start) / (end - start))
  return {
    glint: glintPosition(clock, glint),
    transform: `translate(${move.x} ${move.y}) rotate(${move.turn} 12 12)`,
    strokes: strokes.map(({ length, draw }, index) => {
      const drawn = smooth((clock - draw[0]) / (draw[1] - draw[0]))
      const lifted = smooth((clock - (lift + index * ERASE_STAGGER)) / ERASE_SPAN)
      return length * (1 - drawn) - length * lifted
    }),
    dots: dots.map(({ at }, index) => {
      const shown = smooth((clock - at[0]) / (at[1] - at[0]))
      const erase = lift + (strokes.length + index) * ERASE_STAGGER
      return shown * (1 - smooth((clock - erase) / ERASE_SPAN))
    }),
  }
}

// Each stroke is a dash as long as itself followed by a gap: shifting dashoffset draws or erases it.
export default function DrawnGlyph({ name, size = 44, strokeWidth = 2 }) {
  const names = Array.isArray(name) ? name : [name]
  const single = names.length === 1
  const [turn, setTurn] = useState(0)
  const glyph = GLYPHS[names[turn % names.length]]
  const reducedMotion = usePrefersReducedMotion()
  const gradientRef = useRef(null)
  const groupRef = useRef(null)
  const strokeRefs = useRef([])
  const dotRefs = useRef([])

  const render = useCallback((elapsed) => {
    const progress = elapsed / CYCLE
    if (!single && progress >= 1) {
      setTurn((current) => current + 1)
      return false
    }
    const frame = frameAt(glyph, progress % 1)
    gradientRef.current?.setAttribute('x1', frame.glint.x1)
    gradientRef.current?.setAttribute('x2', frame.glint.x2)
    groupRef.current?.setAttribute('transform', frame.transform)
    frame.strokes.forEach((offset, index) => strokeRefs.current[index]?.setAttribute('stroke-dashoffset', offset))
    frame.dots.forEach((opacity, index) => dotRefs.current[index]?.setAttribute('stroke-opacity', opacity))
    return true
  }, [glyph, single])

  useAnimationFrame(render, !reducedMotion)

  const initial = frameAt(glyph, reducedMotion ? DRAWN : 0)

  return (
    <>
      {reducedMotion ? null : <Glint ref={gradientRef} />}
      <svg className="relative overflow-visible" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <g ref={groupRef} transform={initial.transform}>
          {glyph.strokes.map((stroke, index) => (
            <path key={`${turn}-${stroke.d}`} ref={(node) => { strokeRefs.current[index] = node }} d={stroke.d}
              strokeDasharray={`${stroke.length} ${stroke.length}`} strokeDashoffset={initial.strokes[index]} />
          ))}
          {(glyph.dots ?? []).map((dot, index) => (
            <path key={`${turn}-${dot.d}`} ref={(node) => { dotRefs.current[index] = node }} d={dot.d}
              strokeWidth={dot.width ?? strokeWidth} strokeOpacity={initial.dots[index]} />
          ))}
        </g>
      </svg>
    </>
  )
}
